import React from "react";
import { X, Trash2, Plus, Minus } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Link } from "react-router-dom";
import { COLOR_HEX, COLOR_LABELS, STRAPS } from "@/data/watchData";
import { WatchConfigPreview } from "@/components/WatchConfigPreview";

// Strap ids ("steel"/"leather") are a material choice, not a color — map
// them to the color swatch/label they should actually display.
const STRAP_COLOR_FOR = Object.fromEntries(STRAPS.map(s => [s.id, s.color]));

export const CartDrawer = () => {
  const { t, lang, cart, cartOpen, setCartOpen, removeFromCart, updateQuantity, cartTotal } = useApp();

  return (
    <>
      {/* Backdrop */}
      {cartOpen && (
        <div
          data-testid="cart-backdrop"
          onClick={() => setCartOpen(false)}
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
        />
      )}

      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 h-full w-full sm:w-[440px] z-[70] bg-[#21160B] border-l border-white/10 transform transition-transform duration-500 ${
          cartOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <div className="kicker">{t.cart.title}</div>
            <div className="font-heading text-2xl mt-1">{cart.length} {cart.length === 1 ? "Item" : "Items"}</div>
          </div>
          <button
            data-testid="close-cart-button"
            onClick={() => setCartOpen(false)}
            aria-label={t.cart.close}
            className="text-white/70 hover:text-[#C9A36B]"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex flex-col h-[calc(100%-190px)] overflow-y-auto p-6 gap-4">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="text-white/50 mb-6">{t.cart.empty}</div>
              <Link
                to="/configurator"
                onClick={() => setCartOpen(false)}
                data-testid="empty-cart-cta"
                className="btn-ghost"
              >{t.cart.startConfig}</Link>
            </div>
          ) : (
            cart.map(item => (
              <div key={item.id} data-testid={`cart-item-${item.id}`} className="flex gap-4 border border-white/8 bg-[#2C1E10] p-4">
                <div className="w-20 h-24 shrink-0 bg-white flex items-center justify-center overflow-hidden">
                  {item.type === "custom" && item.config ? (
                    <WatchConfigPreview config={item.config} className="w-full h-full p-1" testId={`cart-preview-${item.id}`} />
                  ) : item.image ? (
                    <img src={item.image} alt={item.name} className="w-full h-full object-contain p-1" />
                  ) : (
                    <div className="w-full h-full bg-white/5" />
                  )}
                </div>
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <div className="font-heading text-lg">{item.name}</div>
                    <div className="text-xs text-white/50 font-mono tracking-wider">{item.type === "custom" ? t.cart.customized : "SOMMET"}</div>
                    {item.type === "collection" && item.strap && (
                      <div className="text-[0.65rem] text-white/45 font-mono tracking-widest uppercase mt-1">
                        {t.configurator.strap}: {item.strap === "leather" ? (lang === "de" ? "Leder" : "Leather") : (lang === "de" ? "Edelstahl" : "Steel")}
                      </div>
                    )}
                    {item.config?.engraving && (
                      <div className="text-xs text-[#C9A36B] font-mono mt-1">&laquo;{item.config.engraving}&raquo;</div>
                    )}
                    {item.type === "custom" && item.config && (
                      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2" data-testid={`cart-config-${item.id}`}>
                        {[
                          { key: "case",  label: t.configurator.case,  value: item.config.case },
                          { key: "dial",  label: t.configurator.dial,  value: item.config.dial },
                          { key: "hands", label: t.configurator.hands, value: item.config.hands },
                          { key: "strap", label: t.configurator.strap, value: item.config.strap },
                        ].map(p => {
                          if (!p.value) return null;
                          const colorKey = p.key === "strap" ? (STRAP_COLOR_FOR[p.value] || p.value) : p.value;
                          return (
                            <div key={p.key} className="flex items-center gap-2 min-w-0" data-testid={`cart-config-${p.key}`}>
                              <span
                                className="w-3.5 h-3.5 rounded-full border border-white/25 shrink-0 shadow-sm"
                                style={{ background: COLOR_HEX[colorKey] || "#666" }}
                              />
                              <div className="min-w-0">
                                <div className="text-[0.55rem] font-mono uppercase tracking-widest text-white/40 leading-tight">{p.label}</div>
                                <div className="text-[0.7rem] text-white/85 truncate leading-tight">{COLOR_LABELS[lang]?.[colorKey] || p.value}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <div className="flex items-center border border-white/15">
                      <button
                        data-testid={`decrease-qty-${item.id}`}
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-1.5 text-white/70 hover:text-[#C9A36B]"
                      ><Minus size={12} /></button>
                      <span className="px-3 text-sm">{item.quantity}</span>
                      <button
                        data-testid={`increase-qty-${item.id}`}
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-1.5 text-white/70 hover:text-[#C9A36B]"
                      ><Plus size={12} /></button>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm">CHF {(item.price * item.quantity).toFixed(2)}</span>
                      <button
                        data-testid={`remove-item-${item.id}`}
                        onClick={() => removeFromCart(item.id)}
                        className="text-white/40 hover:text-red-400"
                        aria-label={t.cart.remove}
                      ><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {cart.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10 bg-[#21160B]">
            <div className="flex justify-between mb-2 text-sm">
              <span className="text-white/60">{t.cart.subtotal}</span>
              <span className="font-mono">CHF {cartTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mb-4 text-sm">
              <span className="text-white/60">{t.cart.shipping}</span>
              <span className="font-mono text-[#C9A36B]">{t.cart.free}</span>
            </div>
            <div className="flex justify-between mb-6 text-lg border-t border-white/10 pt-4">
              <span className="font-heading">{t.cart.total}</span>
              <span data-testid="cart-total" className="font-mono text-[#C9A36B]">CHF {cartTotal.toFixed(2)}</span>
            </div>
            <Link
              to="/checkout"
              onClick={() => setCartOpen(false)}
              data-testid="checkout-button"
              className="btn-champagne w-full"
            >{t.cart.checkout}</Link>
          </div>
        )}
      </aside>
    </>
  );
};
